/**
 * @fileoverview Virtual Test Center Application Store
 * 
 * This Zustand store manages the core application state for the Virtual Test Center (VTC).
 * It handles authentication, room management, drawer navigation, and UI state.
 * 
 * **Key Responsibilities:**
 * - User authentication and session persistence
 * - Room selection and access validation
 * - Side drawer state management (checkin, focus, settings, options)
 * - Proctor list management
 * - Page loader and notification messages
 * - Sound preferences
 * 
 * **State Persistence:**
 * - Authentication token stored in sessionStorage
 * - Room SID stored in sessionStorage
 * - User data maintained in memory during session
 * 
 * **Related Stores:**
 * - `useSessionStore` - Manages individual test session data
 * - `useLayoutStore` - Manages UI layout calculations
 * - `useDataBroker` - Handles data synchronization mode
 * 
 * @module stores/app
 * @requires zustand
 * @requires moment
 */

import { create } from 'zustand';
import config, { ROUTES } from '../config';
import {
  updateNavigation,
  replacePathPlaceholders,
} from '../helpers/utilities';
import moment from 'moment';

import LogRocket from 'logrocket';
import { checkAuthentication, getRooms, getRoom, exitRoom, joinRoom, getIamPermissions } from '../lib/api';
import useSessionStore from './session';
import useDataBroker from './dataBroker';
import useLayoutStore from './layout';
import { MediaAgent } from '@smarterservices/media-agent';
import { Room } from 'livekit-client';

/**
 * @typedef {Object} User
 * @property {string|null} sid - User's unique identifier
 * @property {string|null} firstName - User's first name
 * @property {string|null} lastName - User's last name
 * @property {string|null} token - Authentication token
 * @property {string|null} accountSid - Proctor account identifier
 * @property {string|null} identity - User's identity (typically same as sid)
 * @property {string|null} email - User's email address
 * @property {string} [entered] - ISO timestamp when user entered
 */

/**
 * @typedef {Object} PageLoader
 * @property {boolean} enabled - Whether the page loader is active
 * @property {string|null} text - Message to display in the loader
 */

/**
 * @typedef {Object} LoginScreenMessage
 * @property {string} message - Message text to display
 * @property {'error'|'success'|'info'|'warning'} type - Message type/variant
 */

/**
 * Virtual Test Center Application Store
 * 
 * Manages the core application state for the VTC including:
 * - User authentication and session management
 * - Room selection and configuration
 * - Drawer navigation (checkin, focus, settings, options)
 * - Proctor management
 * - UI state (page loader, sounds, messages)
 * 
 * @module useAppStore
 */
const useAppStore = create((set, get) => ({
  // ============================================================================
  // STATE
  // ============================================================================

  /** @type {string|null} Current room's unique identifier */
  roomSid: null,

  /** @type {Object|null} Current room data including name, settings, and metadata */
  room: null,

  rooms: [],

  locations: [],

  /** @type {User} Current authenticated user information */
  user: {
    sid: null,
    firstName: null,
    lastName: null,
    token: null,
    accountSid: null,
    identity: null,
  },

  /** @type {Object} IAM permissions policy document */
  iam: {
    iam: {
      Version: '2012-10-17',
      Statement: []
    }
  },

  /** @type {Object} IAM default parts for resource string construction */
  iamDefaultParts: {
    partition: 'ss',
    service: 'sp',
    region: '',
    account: '',
  },

  /** @type {boolean} Whether the application is running as a desktop app (Electron) */
  isDesktopApp: false,

  /** @type {number|null} Time offset for synchronization (if needed) */
  timeOffset: null,

  /** @type {string|null} Current proctor user identifier */
  proctorUserSid: null,

  /** @type {number} Width of the checkin drawer in pixels */
  checkinDrawerWidth: 300,

  /** @type {boolean} Whether the side drawer is currently open */
  isDrawerOpen: false,

  /** @type {number} Current width of the drawer in pixels */
  drawerWidth: 0,

  /** @type {'testing'|'checkin'|'focus'|'settings'|'options'} Current drawer mode/view */
  drawerMode: 'testing',

  /** @type {Function|null} Optional confirmation function called before drawer closes */
  drawerCloseConfirmation: null,

  /** @type {boolean} Whether sound notifications are enabled */
  allowSounds: true,

  /** @type {Object|null} Sound to play - triggers sound component to play the specified sound with options */
  soundToPlay: null,

  /** @type {boolean} Whether the settings modal is open */
  isSettingsModalOpen: false,

  /** @type {boolean} Whether the room chat modal is open */
  isRoomChatModalOpen: false,

  /** @type {number} Unread room chat message count */
  unreadRoomChatCount: 0,

  isRoomChatEnabled: false,

  openReplayTracker: null,
  openReplayTrackerAssist: null,

  /** 
   * @type {Object} Audio device settings
   * @property {string|null} inputDeviceId - Selected microphone device ID
   * @property {string|null} inputDeviceLabel - Selected microphone device label
   * @property {string|null} outputDeviceId - Selected speaker device ID
   * @property {string|null} outputDeviceLabel - Selected speaker device label
   */
  audioDevices: {
    inputDeviceId: null,
    inputDeviceLabel: null,
    outputDeviceId: null,
    outputDeviceLabel: null,
  },

  /** 
   * @type {Object} Video magnification settings
   * @property {boolean} enabled - Whether magnification is enabled
   * @property {number} level - Magnification level (e.g., 2 = 2x zoom)
   * @property {number} lensSize - Size of magnification lens in pixels
   */
  magnification: {
    enabled: false,
    level: 4,
    lensSize: 200,
  },

  /** @type {LoginScreenMessage|null} Message to display on login screen */
  loginScreenMessage: null,

  /** @type {Object.<string, Object>} Map of proctor user SIDs to proctor data */
  proctors: {},

  /** @type {PageLoader} Page loader state and message */
  pageLoader: { enabled: false, text: null },

  // ============================================================================
  // ACTIONS
  // ============================================================================

  /**
   * Triggers a sound to be played by the Sounds component
   * 
   * @function playSound
   * @param {string} soundName - The name of the sound to play (e.g., 'sign-on')
   * @param {Object} [options] - Optional configuration for the sound playback
   * @param {number} [options.volume] - Volume level (0.0 to 1.0)
   * @param {number} [options.playbackRate] - Playback speed (0.5 to 4.0)
   * @param {boolean} [options.interrupt] - Whether to interrupt currently playing sound
   * @param {Function} [options.onend] - Callback when sound finishes playing
   * @returns {void}
   * 
   * @example
   * // Play with default options
   * useAppStore.getState().playSound('sign-on');
   * 
   * @example
   * // Play with custom volume
   * useAppStore.getState().playSound('sign-on', { volume: 0.8 });
   * 
   * @example
   * // Play with callback
   * useAppStore.getState().playSound('sign-on', { 
   *   volume: 0.5, 
   *   onend: () => console.log('Sound finished') 
   * });
   */
  playSound: (soundName, options = {}) =>
    set(() => ({
      soundToPlay: { name: soundName, options },
    })),

  /**
   * Clears the sound trigger after it has been played
   * 
   * @function clearSound
   * @returns {void}
   */
  clearSound: () =>
    set(() => ({
      soundToPlay: null,
    })),

  /**
   * Initialize the application with configuration options
   * 
   * Sets up initial application state including desktop app detection
   * and other configuration options.
   * 
   * @function initialize
   * @param {Object} options - Configuration options
   * @param {boolean} [options.isDesktopApp=false] - Whether the app is running as a desktop app (Electron)
   * @returns {void}
   * 
   * @example
   * // Initialize as desktop app
   * useAppStore.getState().initialize({ isDesktopApp: true });
   * 
   * @example
   * // Initialize as web app (default)
   * useAppStore.getState().initialize({ isDesktopApp: false });
   */
  initialize: async (options = {}) => {

    // if window.electronAPI exists, set isDesktopApp to true
    if (typeof window !== 'undefined' && window.electronAPI) {
      set({ isDesktopApp: true });
    }
    console.log('Initializing app store');
    LogRocket.init('cykpw8/vtc');

    // Only initialize the tracker if it hasn't been initialized already
    /*
    if (!get().openReplayTracker && config.proctoring.ui.openReplay) {
      try {
        const tracker = new Tracker({
          projectKey: config.proctoring.ui.openReplay.projectKey,
          __DISABLE_SECURE_MODE: config.proctoring.ui.openReplay.disableSecureMode,
          captureIFrames: true,
          inlineCss: 2,
          canvas: {
            enabled: true,
            disableCanvas: false,
          },
          network: {
            failuresOnly: true,
            capturePayload: true,
          },
        });

        // Initialize assist plugin
        const assistOptions = {};
        tracker.use(trackerAssist(assistOptions));

        // Start the tracker
        tracker.start();

        //tracker.setMetadata('version', __APP_VERSION__);
        //tracker.setMetadata('mode', __APP_MODE__);

        // Save the tracker instance
        set({ openReplayTracker: tracker });

        get().identifyUser();
        await get().loadRoomsAndLocations();
      } catch (error) {
        console.error('Failed to initialize OpenReplay tracker:', error);
      }
    }
      */
  },

  identifyUser: () => {
    if (get().user) {
      LogRocket.identify(get().user.sid, {
        name: get().user.firstName + ' ' + get().user.lastName,
        email: get().user.email,

        // Add your own custom user variables here, ie:
        //subscriptionType: 'pro'
      });
    }

    if (get().openReplayTracker && get().user) {
      const userData = get().user;
      //get().openReplayTracker.setUserID(userData.sid);
      Object.keys(userData).forEach(key => {
        //get().openReplayTracker.setMetadata(key, userData[key]);
      });
    }
  },

  /**
   * Authenticates a user with the provided token
   * 
   * Validates the token with the backend, stores it in sessionStorage,
   * and populates the user state with account information.
   * 
   * @async
   * @param {string} token - Authentication token to validate
   * @returns {Promise<{successful: boolean}>} Result object indicating success/failure
   * 
   * @example
   * const result = await authenticate('eyJhbGc...');
   * if (result.successful) {
   *   // User is authenticated
   * }
   */
  authenticate: async (token) => {
    const returnObj = { successful: false };

    const authCheck = await checkAuthentication(token);

    if (!authCheck.successful) {
      return returnObj;
    } else {
      // if the user is already setup, then we can just return...
      if (get()?.user?.sid) {
        returnObj.successful = true;
        return returnObj;
      }

      // authentication was successful, setup the session store...

      // Put validated params into storage
      sessionStorage.setItem(config.vtc.authentication.localStorageToken, token);

      const userData = {
        sid: authCheck.data.proctorAccountUserSid,
        firstName: authCheck.data.firstName,
        lastName: authCheck.data.lastName,
        email: authCheck.data.email,
        accountSid: authCheck.data.proctorAccountSid,
        token: token,
        identity: authCheck.data.proctorAccountUserSid,
        entered: moment.utc().toISOString(),
      };

      set({ user: userData });

      get().identifyUser();

      // Fetch IAM permissions
      const iamResult = await getIamPermissions();
      if (iamResult.successful) {
        set({ iam: iamResult.data });
      } else {
        console.warn('Failed to fetch IAM permissions:', iamResult.error);
      }

      returnObj.successful = true;
    }
    return returnObj;
  },

  loadRoomsAndLocations: async () => {
    const roomResult = await getRooms();

    if (roomResult.successful) {
      set({ rooms: roomResult.data.rooms, locations: roomResult.data.locations });
    } else {
      // TODO: Add error handling
    }
  },

  /**
   * Sets the current room and validates user access
   * 
   * Fetches room data from the API and validates that:
   * - The room belongs to the user's account
   * - The user has permission to access the room (owner or shared)
   * 
   * Stores the room SID in sessionStorage for persistence.
   * 
   * @async
   * @param {string} sid - Room unique identifier
   * @returns {Promise<{successful: boolean, message?: string}>} Result with success status and optional error message
   * 
   * @example
   * const result = await setRoomSid('RM123456');
   * if (!result.successful) {
   *   console.error(result.message);
   * }
   */
  joinRoom: async (roomSid) => {
    const response = { successful: false };

    // ensure the roomSid is a valid roomSid...
    if (!roomSid || !roomSid.startsWith('PR')) {
      console.error('Invalid room SID', roomSid);
      return;
    }

    await get().loadRoomsAndLocations();

    // get the room data from the preloaded rooms...
    const room = get().rooms.find((room) => room.sid === roomSid);
    if (!room) {
      response.message = 'Room not found';
      return response;
    }

    // first fetch the room...
    const roomJoinResponse = await joinRoom(get().user.accountSid, room.proctorAccountLocationSid, roomSid, get().user.sid);

    if (roomJoinResponse.successful) {

      set({ roomSid: roomJoinResponse.data.room.sid, room: roomJoinResponse.data.room });
      sessionStorage.setItem(config.vtc.authentication.localStorageRoomSid, roomJoinResponse.data.room.sid);

      // build the room media connection now...
      await get().buildRoomMediaConnection(roomJoinResponse.data.room.sid);

      response.successful = true;
    } else {
      response.message =
        roomJoinResponse.error.message;
    }

    return response;
  },

  /**
   * Exits the current room without logging out
   * 
   * Notifies the backend, clears room state, and navigates to rooms list.
   * User remains authenticated.
   * 
   * @async
   * @returns {Promise<void>}
   */
  exitRoom: async () => {
    get().showPageLoader('Exiting room...');
    const accountSid = get().user.accountSid;
    const roomSid = get().roomSid;
    const room = get().room;

    // Cleanup event listeners and connections before exiting
    useDataBroker.getState().cleanup();

    set({ roomSid: null, room: null });
    sessionStorage.removeItem(config.vtc.authentication.localStorageRoomSid);

    if (room && room.proctorAccountLocationSid) {
      await exitRoom(accountSid, room.proctorAccountLocationSid, roomSid);
      updateNavigation(replacePathPlaceholders(ROUTES.ROOMS));
    }
  },

  buildRoomMediaConnection: async (roomSid) => {
    // Guard: Don't re-initialize if already connected to this room
    const existingMediaClient = get().mediaClient;
    const existingRoomSid = get().roomSid;

    // if this connection is already set, then just return...
    if (existingMediaClient && existingRoomSid === roomSid) {
      return;
    }

    const clientConfig = {
      apiUrl: config.proctoring?.api?.baseUrl,
      roomId: roomSid,
      identity: get().user.sid,
      token: get().user.token,
      websocketUrl: config.media?.livekit?.websocketUrl,
      type: 'proctor',
    };

    const clientInstance = await MediaAgent(clientConfig);
    const roomInstance = new Room();
    const mediaClient = clientInstance;
    clientInstance.setRoom(roomInstance);

    // update this in the openreplay tracker
    if (get().openReplayTracker) {
      get().openReplayTracker.setMetadata('roomSid', roomSid);
    }

    set({
      mediaClient,
      mediaRoom: roomInstance,
    });
  },

  /**
   * Updates the user state with provided user data
   * 
   * @async
   * @param {User} userData - User data object to set
   * @returns {Promise<void>}
   */
  setUser: async (userData) => {
    set({ user: userData });
  },

  /**
   * Displays the full-page loader with a custom message
   * 
   * @param {string} message - Message to display in the loader
   * @returns {void}
   * 
   * @example
   * showPageLoader('Loading room data...');
   */
  showPageLoader: (message) => {
    set({ pageLoader: { enabled: true, text: message } });
  },

  /**
   * Hides the full-page loader
   * 
   * @returns {void}
   */
  clearPageLoader: () => {
    set({ pageLoader: { enabled: false, text: null } });
  },

  /**
   * Removes a proctor from the active proctors list
   * 
   * @param {string} proctorUserSid - Proctor user identifier to remove
   * @returns {void}
   * 
   * @example
   * removeProctor('PR123456');
   */
  removeProctor: (proctorUserSid) =>
    set((state) => {
      const proctors = { ...state.proctors };

      delete proctors[proctorUserSid];

      return {
        proctors,
      };
    }),

  /**
   * Adds a proctor to the active proctors list
   * 
   * Will not add the current user to prevent self-listing.
   * 
   * @param {string} proctorUserSid - Proctor user identifier
   * @param {Object} data - Proctor data (name, status, etc.)
   * @returns {void}
   * 
   * @example
   * addProctor('PR123456', { name: 'John Doe', status: 'active' });
   */
  addProctor: (proctorUserSid, data) =>
    set((state) => {
      const proctors = { ...state.proctors };

      // But do not add yourself...
      //if (get()?.user?.sid !== proctorUserSid) {
      proctors[proctorUserSid] = data;
      //}

      return {
        proctors,
      };
    }),

  /**
   * Sets a message to display on the login screen
   * 
   * Accepts string, object with message/type, or null to clear.
   * Message persists until explicitly cleared.
   * 
   * @async
   * @param {string|LoginScreenMessage|null} message - Message to display or null to clear
   * @returns {Promise<void>}
   * 
   * @example
   * // String message (defaults to 'error' type)
   * setLoginScreenMessage('Invalid credentials');
   * 
   * // Object with type
   * setLoginScreenMessage({ message: 'Logged out successfully', type: 'success' });
   * 
   * // Clear message
   * setLoginScreenMessage(null);
   */
  setLoginScreenMessage: async (message) => {
    let messageObj = { message: null, type: 'error' };

    if (!message) {
      messageObj = null;
    } else if (typeof message === 'string') {
      messageObj.message = message;
    } else if (typeof message === 'object' && message !== null) {
      if ('message' in message) {
        messageObj.message = message.message;
        if (message.type) {
          messageObj.type = message.type;
        }
      } else {
        messageObj = null;
      }
    } else {
      messageObj = null;
    }
    set({ loginScreenMessage: messageObj });
  },

  /**
   * Enables or disables sound notifications
   * 
   * @async
   * @param {boolean} value - True to enable sounds, false to disable
   * @returns {Promise<void>}
   */
  setAllowSounds: async (value) => {
    set({ allowSounds: value });
  },

  /**
   * Set settings modal open state
   * @param {boolean} isOpen - Whether the settings modal should be open
   */
  setSettingsModalOpen: (isOpen) => {
    set({ isSettingsModalOpen: isOpen });
  },

  /**
   * Sets the room chat modal open state
   * @param {boolean} isOpen - Whether the room chat modal should be open
   */
  setRoomChatModalOpen: (isOpen) => {
    set({ isRoomChatModalOpen: isOpen });
    // Clear unread count when opening the modal
    if (isOpen) {
      set({ unreadRoomChatCount: 0 });
    }
  },

  /**
   * Increments the unread room chat message count
   */
  incrementUnreadRoomChatCount: () => {
    set((state) => ({ unreadRoomChatCount: state.unreadRoomChatCount + 1 }));
  },

  /**
   * Clears the unread room chat message count
   */
  clearUnreadRoomChatCount: () => {
    set({ unreadRoomChatCount: 0 });
  },

  /**
   * Initialize audio devices from localStorage or system defaults
   * 
   * Checks localStorage for previously saved device labels, finds matching devices,
   * and falls back to system defaults if not found.
   * 
   * @async
   * @returns {Promise<void>}
   */
  initializeAudioDevices: async () => {
    try {
      // Get saved device labels from localStorage
      const savedInputLabel = localStorage.getItem(config?.vtc?.media?.microphone?.localStorageToken || 'vtc_audio_input_device');
      const savedOutputLabel = localStorage.getItem(config?.vtc?.media?.speaker?.localStorageToken || 'vtc_audio_output_device');

      // Request permissions first to get device labels
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop());
      } catch (err) {
        console.warn('[AppStore] Could not get microphone permissions:', err);
      }

      // Enumerate all devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(d => d.kind === 'audioinput');
      const audioOutputs = devices.filter(d => d.kind === 'audiooutput');

      // Find saved input device by label or use default
      let inputDevice;
      if (savedInputLabel) {
        inputDevice = audioInputs.find(d => d.label === savedInputLabel);
      }
      if (!inputDevice) {
        inputDevice = audioInputs.find(d => d.deviceId === 'default') || audioInputs[0];
      }

      // Find saved output device by label or use default
      let outputDevice;
      if (savedOutputLabel) {
        outputDevice = audioOutputs.find(d => d.label === savedOutputLabel);
      }
      if (!outputDevice) {
        outputDevice = audioOutputs.find(d => d.deviceId === 'default') || audioOutputs[0];
      }

      // Update store with device info
      set({
        audioDevices: {
          inputDeviceId: inputDevice?.deviceId || null,
          inputDeviceLabel: inputDevice?.label || 'Default Microphone',
          outputDeviceId: outputDevice?.deviceId || null,
          outputDeviceLabel: outputDevice?.label || 'Default Speaker',
        }
      });
    } catch (err) {
      console.error('[AppStore] Failed to initialize audio devices:', err);
    }
  },

  /**
   * Set audio input device
   * 
   * Stores the device label (not ID) to localStorage for persistence across sessions.
   * Device IDs can change, but labels remain consistent.
   * 
   * @param {string} deviceId - Device ID (for current session use)
   * @param {string} deviceLabel - Device label/name (for persistence)
   */
  setAudioInputDevice: (deviceId, deviceLabel) => {
    set(state => ({
      audioDevices: {
        ...state.audioDevices,
        inputDeviceId: deviceId,
        inputDeviceLabel: deviceLabel,
      }
    }));
    // Store the label, not the ID, as IDs can change between sessions
    localStorage.setItem(config?.vtc?.media?.microphone?.localStorageToken, deviceLabel);
  },

  /**
   * Set audio output device
   * 
   * Stores the device label (not ID) to localStorage for persistence across sessions.
   * Device IDs can change, but labels remain consistent.
   * 
   * @param {string} deviceId - Device ID (for current session use)
   * @param {string} deviceLabel - Device label/name (for persistence)
   */
  setAudioOutputDevice: (deviceId, deviceLabel) => {
    set(state => ({
      audioDevices: {
        ...state.audioDevices,
        outputDeviceId: deviceId,
        outputDeviceLabel: deviceLabel,
      }
    }));
    // Store the label, not the ID, as IDs can change between sessions
    localStorage.setItem(config?.vtc?.media?.speaker?.localStorageToken, deviceLabel);
  },

  /**
   * Toggles video magnification on/off
   * 
   * When enabled, hovering over any video will display a magnified view.
   * Updates magnification in all SessionViewers without causing re-renders.
   * 
   * @function toggleMagnification
   * @returns {void}
   * 
   * @example
   * useAppStore.getState().toggleMagnification();
   */
  toggleMagnification: () => {
    const currentMag = get().magnification;
    const newEnabled = !currentMag.enabled;

    set((state) => ({
      magnification: {
        ...state.magnification,
        enabled: newEnabled,
      },
    }));

    // Update magnification in all SessionViewers using their internal store
    const sessions = useSessionStore.getState().sessions;
    Object.keys(sessions).forEach((sessionSid) => {
      const viewerRef = sessions[sessionSid]?.viewerRef;
      if (viewerRef?.current?.updateMagnification) {
        viewerRef.current.updateMagnification({
          enabled: newEnabled,
          level: currentMag.level,
          lensSize: currentMag.lensSize,
        });
      }
    });
  },

  /**
   * Sets magnification enabled state
   * 
   * Updates magnification in all SessionViewers without causing re-renders.
   * 
   * @function setMagnificationEnabled
   * @param {boolean} enabled - Whether to enable magnification
   * @returns {void}
   * 
   * @example
   * useAppStore.getState().setMagnificationEnabled(true);
   */
  setMagnificationEnabled: (enabled) => {
    const currentMag = get().magnification;

    set((state) => ({
      magnification: {
        ...state.magnification,
        enabled,
      },
    }));

    // Update magnification in all SessionViewers using their internal store
    const sessions = useSessionStore.getState().sessions;
    Object.keys(sessions).forEach((sessionSid) => {
      const viewerRef = sessions[sessionSid]?.viewerRef;
      if (viewerRef?.current?.updateMagnification) {
        viewerRef.current.updateMagnification({
          enabled,
          level: currentMag.level,
          lensSize: currentMag.lensSize,
        });
      }
    });
  },

  /**
   * Updates magnification settings
   * 
   * @function updateMagnification
   * @param {Object} settings - Magnification settings
   * @param {boolean} [settings.enabled] - Whether magnification is enabled
   * @param {number} [settings.level] - Magnification level
   * @param {number} [settings.lensSize] - Lens size in pixels
   * @returns {void}
   * 
   * @example
   * useAppStore.getState().updateMagnification({ enabled: true, level: 3, lensSize: 250 });
   */
  updateMagnification: (settings) => {
    set((state) => ({
      magnification: {
        ...state.magnification,
        ...settings,
      },
    }));
  },

  /**
   * Logs out the current user
   * 
   * Performs the following:
   * - Exits the current room (if in one)
   * - Clears user state and authentication token
   * - Removes all sessionStorage data
   * - Displays logout message
   * - Redirects to login screen
   * 
   * @async
   * @param {Object} [options={}] - Logout options
   * @param {LoginScreenMessage} [options.loginScreenMessage] - Custom message to display on login screen
   * @returns {Promise<void>}
   * 
   * @example
   * // Default logout
   * await logout();
   * 
   * // With custom message
   * await logout({
   *   loginScreenMessage: { message: 'Session expired', type: 'warning' }
   * });
   */
  logout: async (options = {}) => {
    get().showPageLoader('Logging out...');
    // Store these so they can be used for the exitRoom call...
    const accountSid = get().user.accountSid;
    const roomSid = get().roomSid;
    const userSid = get().user.sid;

    // Cleanup event listeners and connections before logging out
    useDataBroker.getState().cleanup();

    await get().exitRoom();

    // clear from the room first to ensure the event emitter is not triggered after exiting...
    set({ roomSid: null, room: null });


    // now disconnect from the media room.
    if (get().mediaClient) {
      try {
        await get().mediaClient.disconnectFromRoom();
      } catch (err) {
        console.error('[AppStore] Failed to disconnect from media room:', err);
      }
    }
    // now clear the user information. This has to com after exitRoom b/c the user token is needed for the api call...
    set({ user: null });
    // Clear all values from session Storage...
    sessionStorage.removeItem(config.vtc.authentication.localStorageToken);
    sessionStorage.removeItem(config.vtc.authentication.localStorageRoomSid);

    if (options?.loginScreenMessage) {
      get().setLoginScreenMessage(options.loginScreenMessage);
    } else {
      get().setLoginScreenMessage({
        message: 'You were successfully logged out.',
        type: 'success',
      });
    }

    // kick back to login...
    updateNavigation(replacePathPlaceholders(ROUTES.LOGIN));
  },

  /**
   * Sets a confirmation function to be called before drawer closes
   * 
   * Useful for preventing accidental closure when unsaved changes exist.
   * The function should handle its own confirmation UI.
   * 
   * @async
   * @param {Function|null} fn - Confirmation function or null to remove
   * @returns {Promise<void>}
   * 
   * @example
   * setDrawerCloseConfirmation(() => {
   *   if (hasUnsavedChanges) {
   *     return confirm('You have unsaved changes. Close anyway?');
   *   }
   *   return true;
   * });
   */
  setDrawerCloseConfirmation: async (fn) => {
    set({ drawerCloseConfirmation: fn });
  },

  /**
   * Opens the side drawer in the specified mode
   * 
   * Handles drawer mode transitions and calls confirmation if switching modes.
   * Updates navigation to reflect the drawer state in the URL.
   * 
   * @returns {boolean} False if drawer opening was prevented by confirmation, otherwise undefined
   * 
   * @example
   * // Open checkin drawer
   * openDrawer('checkin');
   * 
   * // Open focus drawer for specific session
   * openDrawer('focus', 'SS123456');
   * 
   * // Open focus drawer with specific tab
   * openDrawer('focus', 'SS123456', { initialTab: 'communication' });
   */
  openDrawer: async (drawerMode, sessionSid, options = {}) => {
    if (get().isDrawerOpen && drawerMode !== get().drawerMode) {
      // if there is a confirmation function registered, make sure we call it first...
      if (get().drawerCloseConfirmation) {
        const wasClosed = await get().closeDrawer(false);
        if (!wasClosed) {
          return false;
        }
      } else {
        // there is not confirmation so just close the drawer
        //get().closeDrawer(false);
      }
    }
    let navRoute, navOptions;
    let drawerWidth = '0px';

    if (drawerMode === 'checkin') {
      drawerWidth = config.vtc.modes.checkin.drawerWidth;
      navRoute = ROUTES.CHECKIN;
      navOptions = { roomSid: get().roomSid };
    } else if (drawerMode === 'focus') {
      drawerWidth = config.vtc.modes.focus.drawerWidth;
      // set the focused session sid
      // Note: setFocusedSession verifies claim in background and will close drawer if it fails
      await useSessionStore.getState().setFocusedSession(sessionSid);

      navRoute = ROUTES.SESSION_FOCUS;
      navOptions = { roomSid: get().roomSid, sessionSid: sessionSid };

      // NOTE: Removed enableComponents call here because SessionViewerPool already handles
      // setting the correct enabledComponents via props. Calling it here was causing
      // a race condition with hardcoded config overriding the proper config from
      // getEnabledComponentsForSession, resulting in hamburger menu and re-renders.

      // If an initial tab was specified, set it
      const viewerRef = useSessionStore.getState().sessions[sessionSid]?.viewerRef;
      if (viewerRef?.current && options.initialTab && viewerRef.current.setSessionDetailsTab) {
        viewerRef.current.setSessionDetailsTab(options.initialTab);
      }


    } else if (drawerMode === 'settings') {
      drawerWidth = config.vtc.modes.settings.drawerWidth;
      navRoute = ROUTES.SETTINGS;
      navOptions = { roomSid: get().roomSid };
    } else if (drawerMode === 'options') {
      drawerWidth = config.vtc.modes.options.drawerWidth;
      navRoute = ROUTES.OPTIONS;
      navOptions = { roomSid: get().roomSid };
    }

    set({
      drawerWidth: drawerWidth,
      isDrawerOpen: true,
      drawerMode: drawerMode,
    });

    updateNavigation(replacePathPlaceholders(navRoute, navOptions));
  },

  /**
   * Closes the side drawer
   * 
   * Calls confirmation function if one is registered. If in 'focus' mode,
   * clears the focused session. Updates URL unless disabled.
   * 
   * @param {boolean} [triggerUrlUpdate=true] - Whether to update the URL after closing
   * @returns {boolean} False if closure was prevented by confirmation, otherwise undefined
   * 
   * @example
   * // Close drawer and update URL
   * closeDrawer();
   * 
   * // Close drawer without URL update (e.g., during navigation)
   * closeDrawer(false);
   */
  closeDrawer: async (triggerUrlUpdate = true) => {
    // if there is a confirmation function registered, make sure we call it first...
    if (get().drawerCloseConfirmation) {
      // there is, so fire it and return false...
      get().drawerCloseConfirmation();
      return false;
    }


    if (get().drawerMode === 'focus') {
      // since the drawer closed, clear the focused session, don't retrigger close drawer...
      await useSessionStore.getState().clearFocusedSession({ closeDrawer: false });
    }

    set({
      isDrawerOpen: false,
      drawerMode: '',
      drawerWidth: '0px',
    });

    // Defer layout update to not block drawer animation
    // Use requestIdleCallback to run during browser idle time, or setTimeout as fallback
    if (typeof window !== 'undefined' && window.requestIdleCallback) {
      requestIdleCallback(() => {
        useLayoutStore.getState().debouncedLayoutUpdate();
      });
    } else {
      setTimeout(() => {
        useLayoutStore.getState().debouncedLayoutUpdate();
      }, 0);
    }

    if (triggerUrlUpdate) {
      updateNavigation(
        replacePathPlaceholders(ROUTES.ROOM, { roomSid: get().roomSid })
      );
    }
  },

  /**
   * Constructs an IAM resource string in the SSRN format
   * 
   * @function getIamResourceString
   * @param {string} resource - The resource identifier (e.g., 'session/SS123', 'room/RM456')
   * @param {Object} [overrides={}] - Optional overrides for default parts
   * @param {string} [overrides.partition] - Override partition (default: 'ss')
   * @param {string} [overrides.service] - Override service (default: 'sp')
   * @param {string} [overrides.region] - Override region (default: '')
   * @param {string} [overrides.account] - Override account (default: '')
   * @returns {string} Formatted IAM resource string
   * 
   * @example
   * // Returns: 'ssrn:ss:sp:::session/SS123456'
   * getIamResourceString('session/SS123456')
   * 
   * @example
   * // Returns: 'ssrn:ss:sp:us-east-1:account123:room/RM789'
   * getIamResourceString('room/RM789', { region: 'us-east-1', account: 'account123' })
   */
  getIamResourceString: (resource, overrides = {}) => {
    const defaultParts = get().iamDefaultParts;
    const finalPartition = overrides.partition || defaultParts.partition;
    const finalService = overrides.service || defaultParts.service;
    const finalRegion = overrides.region || defaultParts.region;
    const finalAccount = overrides.account || get()?.user?.accountSid || defaultParts.account;

    return `ssrn:${finalPartition}:${finalService}:${finalRegion}:${finalAccount}:${resource}`;
  },

  // ============================================================================
  // DEPRECATED / UNUSED METHODS
  // ============================================================================
  // The following methods are commented out and not currently in use.
  // They may be removed in a future refactor or re-enabled if needed.

  /*
  setSessionDimensions: (width, height) => {
    set({ sessionDimensions: { width, height } });
  },

  setTestingAreaDimensions: (width, height) => {
    set({ testingAreaDimensions: { width, height } });
    //get().calculateLayoutSizing();
  },

  setRoomDimensions: (width, height) => {
    if (width && height) {
      set({ roomDimensions: { width, height } });
    }
  },
  */
}));

/**
 * Virtual Test Center Application Store Hook
 * 
 * @exports useAppStore
 * @type {import('zustand').UseBoundStore<import('zustand').StoreApi<Object>>}
 */
export default useAppStore;
